import {useToast} from '@chakra-ui/react';
import {useCartContext} from 'components/providers';
import {useCallback, useEffect} from 'react';
import {ICartItem} from 'types';

import {validateMetroProductsStock} from '../api';

const VALIDATION_INTERVAL = 15 * 60 * 1000; // 15 minutes in milliseconds
const LAST_VALIDATION_KEY = 'metro_stock_last_validation';

export const useMetroStockValidation = () => {
  const {cartItems, deleteProduct, cartProvider} = useCartContext();
  const toast = useToast();

  /**
   * Validates the stock of Metro products and removes out-of-stock items from cart
   */
  const validateAndRemoveOutOfStock = useCallback(async () => {
    const productIds = cartItems.map((item) => item.productId);

    try {
      const response = await validateMetroProductsStock(productIds);
      const outOfStockProducts = response.products.filter((product) => !product.in_stock);

      if (outOfStockProducts.length > 0) {
        const removedItems: ICartItem[] = [];

        outOfStockProducts.forEach((outOfStockProduct) => {
          const cartItem = cartItems.find((item) => item.productId === outOfStockProduct.id);
          if (cartItem) {
            removedItems.push(cartItem);
            deleteProduct(cartItem.id);
          }
        });

        const removedItemsCount = removedItems.length;
        const itemText = removedItemsCount === 1 ? 'item' : 'items';

        toast({
          title: 'Items removed from cart',
          description: `${removedItemsCount} ${itemText} removed due to stock unavailability`,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });

        return {success: true, removedItems};
      }

      return {success: true, removedItems: []};
    } catch (error) {
      console.error('Error validating Metro product stock:', error);

      toast({
        title: 'Error validating stock',
        description: 'Failed to validate product availability',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });

      return {success: false, removedItems: []};
    }
  }, [cartItems, deleteProduct, toast]);

  /**
   * Checks if enough time has passed since last validation
   */
  const shouldValidateStock = useCallback(() => {
    const lastValidation = localStorage.getItem(LAST_VALIDATION_KEY);
    if (!lastValidation) return true;

    const timeSinceLastValidation = Date.now() - parseInt(lastValidation, 10);
    return timeSinceLastValidation >= VALIDATION_INTERVAL;
  }, []);

  /**
   * Updates the last validation timestamp
   */
  const updateValidationTimestamp = useCallback(() => {
    localStorage.setItem(LAST_VALIDATION_KEY, Date.now().toString());
  }, []);

  /**
   * Validates stock with time-based throttling
   */
  const validateWithThrottling = useCallback(async () => {
    if (!shouldValidateStock()) return;

    const result = await validateAndRemoveOutOfStock();
    if (result.success) {
      updateValidationTimestamp();
    }
  }, [shouldValidateStock, validateAndRemoveOutOfStock, updateValidationTimestamp]);

  useEffect(() => {
    if (cartProvider?.slug === 'metro' && cartItems.length > 0) {
      validateWithThrottling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartProvider?.slug]);
};
